/* eslint-disable no-prototype-builtins */
// ref: https://jsfiddle.net/cowbellerina/zbfh96b1/
import _ from 'lodash';

const getConditionRule = (conditionRule) => {
  let rule = [];
  let invert;

  // foo!=bar
  if (conditionRule.indexOf('!=') !== -1) {
    rule = conditionRule.split('!=');
    invert = true;
  } else if (conditionRule.indexOf('=') !== -1) {
    // foo=bar
    rule = conditionRule.split('=');
    invert = false;
  }

  if (rule.length !== 2) {
    return false;
  }

  const [field, values] = rule;
  return {
    invert,
    field,
    values: values.split(','),
  };
};

function getConditionRules(condition = '') {
  let rules = [];
  let allHaveToMatch = false;
  let visible = false;

  // foo=bar || bar=foo
  if (condition.indexOf('||') !== -1) {
    rules = condition.split('||');
    allHaveToMatch = false;
    visible = false;
  } else if (condition.indexOf('&&') !== -1) {
    // foo=bar && bar=foo
    rules = condition.split('&&');
    allHaveToMatch = true;
    visible = true;
  } else {
    // foo=bar
    rules = [condition];
    allHaveToMatch = true;
    visible = true;
  }

  return {
    rules,
    allHaveToMatch,
    visible,
  };
}

/**
 * @description Evaluate expressions in the custom `condition` field, removing fields whose conditions
 * evaluate to false. Makes updates to the form data and schema based on the removed fields.
 * This is mostly taken from the "advanced conditionals" example on react-jsonschema-forms docs https://jsfiddle.net/cowbellerina/zbfh96b1/
 */
export const processForm = (
  originalSchema,
  originalUISchema,
  schema,
  uiSchema,
  formData,
) => {
  let cleanFormData = _.omitBy(formData, _.isNil);

  const conditionalFields = _.pickBy(uiSchema, (field) =>
    field.hasOwnProperty('condition'),
  );

  if (_.isEmpty(conditionalFields)) {
    return {
      schema,
      uiSchema,
      formData: cleanFormData,
    };
  }

  const newSchema = _.assign({}, schema);
  const newUISchema = _.assign({}, uiSchema);

  _.each(conditionalFields, (dependantSchema, dependant) => {
    const { rules, allHaveToMatch } = getConditionRules(
      dependantSchema.condition,
    );
    const matches = [];
    _.each(rules, (rule) => {
      const { field, values: stringValues, invert } = getConditionRule(rule);
      let visible = invert;
      const values = stringValues.map((value) => {
        switch (value) {
          case 'true':
            return true;
          case 'false':
            return false;
          default:
            return value;
        }
      });

      if (field && cleanFormData.hasOwnProperty(field)) {
        const currentValues = _.isArray(cleanFormData[field])
          ? cleanFormData[field]
          : [cleanFormData[field]];
        _.each(values, (value) => {
          if (invert) {
            visible = visible && _.indexOf(currentValues, value) === -1;
          } else {
            visible = visible || _.indexOf(currentValues, value) !== -1;
          }
        });
      }

      matches.push(visible);
    });
    // Add or remove conditional field from schema
    let shouldBeVisible = false;
    if (matches.length) {
      shouldBeVisible = allHaveToMatch
        ? // foo=bar && bar=foo
          _.every(matches, Boolean)
        : // foo=bar || bar=foo
          _.some(matches, Boolean);
    }

    if (shouldBeVisible) {
      newSchema.properties[dependant] = originalSchema.properties[dependant];
    } else {
      newSchema.properties = _.omit(newSchema.properties, [dependant]);
      cleanFormData = _.omit(cleanFormData, [dependant]);
    }
  });

  // Update UI Schema UI order
  // react-jsonschema-form cannot handle extra properties found in UI order
  newUISchema['ui:order'] = _.intersection(
    originalUISchema['ui:order'],
    _.keys(newSchema.properties),
  );
  // Update Schema required fields
  if (originalSchema.hasOwnProperty('required')) {
    newSchema.required = _.intersection(
      originalSchema.required,
      _.keys(newSchema.properties),
    );
  }

  return {
    schema: newSchema,
    uiSchema: newUISchema,
    formData: cleanFormData,
  };
};

export default processForm;
